import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { CarrierPopover } from 'apps/booking/components/CarrierPopover/CarrierPopover'
import { SidebarReservedOfferDetails } from 'apps/booking/components/Sidebar'
import { REPS } from 'apps/booking/constants/reps'
import { ARCHIVED_STATUSES } from 'apps/booking/constants/shipments'
import { updateSidebarShipment } from 'apps/booking/utils/actions/sidebar'
import {
  selectShipmentDetails,
  selectHasInvalidP44TruckNumber,
  selectHasInvalidKtTruckNumber,
} from 'apps/booking/utils/selectors/shipment'
import { ConnectedPhoneNumber } from 'apps/marketplace/components/ConnectedPhoneNumber'
import TruckInlineEdit from 'apps/marketplace/components/TruckInlineEdit'
import VinInlineEdit from 'apps/marketplace/components/VinInlineEdit'
import { InlineEdit } from 'components/forms'
import { DISPLAY } from 'constants/display'
import { USER_GROUPS } from 'constants/userGroups'

const OpsInfo = () => {
  const dispatch = useDispatch()
  const { shipmentPermissions, userDetails } = useUserDetails()
  const { permissions } = shipmentPermissions.find(({ key }) => key === 'transfix_manager') || {}
  const { groups: userGroups } = userDetails || {}
  const shipment = useSelector(selectShipmentDetails)

  if (!userGroups) return <></>
  const cxRep = {
    name: '',
    id: '',
  }
  const { cxTasksAttributes = [], opsRepParentUserId: opsRepId, opsRepName } = shipment
  if (cxTasksAttributes?.length > 0) {
    const cxTask = cxTasksAttributes[0]
    cxRep.name = `${cxTask.cxRepFirstName} ${cxTask.cxRepLastName}`
    cxRep.id = cxTask.cxRepParentUserId
  }

  const handleChange = (selectedId: string, selectedName: string, inputId: string) => {
    const input = REPS[inputId as keyof typeof REPS]
    if (input && shipment.id) {
      if (inputId === REPS.complianceRepEditable.id) {
        let id = ''
        if (selectedId) id = selectedId
        dispatch(
          updateSidebarShipment(shipment.id, {
            coveredBid: {
              ...shipment.coveredBid,
              complianceReview: { ...shipment.coveredBid?.complianceReview, complianceRep: { id } },
            },
          })
        )
      } else {
        dispatch(
          updateSidebarShipment(shipment.id, {
            info: { [input.key]: selectedId, [input.name]: selectedName },
          })
        )
      }
    }
  }

  const groups = userGroups.map(group => group.name)
  const canEditOpsRep = permissions?.editOpsRep
  const canEditComplianceRep = groups.includes(USER_GROUPS.compliance.name)

  const editStatuses = {
    [REPS.opsRepEditable.id]: canEditOpsRep,
    [REPS.complianceRepEditable.id]: canEditComplianceRep,
  }

  let complianceRep

  if (shipment.coveredBid?.complianceReview) {
    ;({
      complianceReview: { complianceRep },
    } = shipment.coveredBid)
  }

  const complianceRepText = complianceRep?.fullName ? complianceRep.fullName : '-'
  const complianceRepId = complianceRep?.id ? complianceRep.id : '-'

  const repValues = {
    [REPS.opsRepEditable.id]: opsRepName,
    [REPS.complianceRepEditable.id]: complianceRepText,
  }

  const repUserIds = {
    [REPS.opsRepEditable.id]: opsRepId,
    [REPS.complianceRepEditable.id]: complianceRepId,
  }

  const repTypes = Object.values(REPS).map(rep => ({
    ...rep,
    shipmentId: shipment.id,
    handleUserChange: handleChange,
    userId: repUserIds[rep.id],
    editable: editStatuses[rep.id],
    value: repValues[rep.id],
    autocomplete: true,
  }))

  return (
    <>
      {repTypes.map(
        ({ autocomplete, editable, handleUserChange, id, label, type, userId, value }) => (
          // TODO: Update to InlineEditContainer
          <>
            <p className='b text-secondary'>{label}</p>
            <p>
              <InlineEdit
                // @ts-ignore
                autocomplete={autocomplete}
                userId={Number(userId)}
                label={label}
                hideLabel
                type={type}
                editable={editable}
                value={value}
                handleUserChange={handleUserChange}
                id={id}
                key={id}
              />
            </p>
          </>
        )
      )}
      <p className='text-secondary b'>AO Rep</p>
      <p>
        {cxRep?.name && (
          <a href={`${__ROOT__}/users/${cxRep.id}`} target='_blank' rel='noopener noreferrer'>
            {cxRep.name}
          </a>
        )}
      </p>
    </>
  )
}

export const People = () => {
  const dispatch = useDispatch()
  const shipmentInfo = useSelector(selectShipmentDetails)
  const showInvalidP44TruckError = useSelector(selectHasInvalidP44TruckNumber)
  const showInvalidKtTruckError = useSelector(selectHasInvalidKtTruckNumber)
  const {
    carrierName,
    carrierIsTrusted,
    carrierId = '',
    driverName,
    driverParentUserId,
    dispatcherName,
    dispatcherParentUserId,
    hasClaims = false,
    dispatcherPhone,
    formattedDriverPhone = '',
    coveredBid,
    status: { key: statusKey } = { key: '' },
    id: shipmentId = 0,
  } = shipmentInfo

  const handleDriverChange = (id: string, name: string) => {
    const params = { info: { has_claims: hasClaims, driver_id: id, driver_name: name } }
    dispatch(updateSidebarShipment(shipmentId, params))
  }

  const isArchived = ARCHIVED_STATUSES.includes(statusKey)
  const showOfferDetails = !isArchived && carrierId
  const showReserveDetails = ['reserved', 'waterfall_offered'].includes(statusKey)

  return (
    <div>
      <h3 className='body-medium mb2'>Assignment</h3>
      <div className='grid'>
        {showOfferDetails && (
          <>
            <div className='group1'>
              {/* Carrier */}
              <p className='text-secondary b'>Carrier</p>
              <a
                href={`${__ROOT__}/carriers/${carrierId}`}
                target='_blank'
                rel='noopener noreferrer'>
                {carrierIsTrusted ? `${carrierName} (Preferred Carrier)` : carrierName}
              </a>
              <SidebarReservedOfferDetails />

              {/* Dispatcher */}
              <div className='mv2'>
                <p className='b text-secondary'>Dispatcher</p>
                {dispatcherParentUserId ? (
                  <>
                    <div>
                      <a
                        href={`${__ROOT__}/users/${dispatcherParentUserId}`}
                        target='_blank'
                        rel='noopener noreferrer'>
                        {dispatcherName}
                      </a>
                    </div>
                    <div>
                      {dispatcherPhone ? (
                        <ConnectedPhoneNumber phoneNumber={dispatcherPhone} />
                      ) : (
                        ''
                      )}
                    </div>
                  </>
                ) : (
                  DISPLAY.none
                )}
              </div>

              {/* Driver */}
              <div className='b text-secondary'>Driver</div>
              <InlineEdit
                // @ts-ignore
                companyId={carrierId}
                value={driverName}
                userId={driverParentUserId}
                label='Driver'
                type='driver'
                handleUserChange={handleDriverChange}
                autocomplete
                hideLabel
                editable
              />
              <div>
                <ConnectedPhoneNumber phoneNumber={formattedDriverPhone} />
              </div>
            </div>
            <div className='truck-vin-info order-3' data-test='truck-vin-info'>
              <div className='mb1'>
                <div className='flex flex-column mb2'>
                  <div>
                    <VinInlineEdit shipmentId={shipmentId} vin={shipmentInfo.vin} />
                  </div>
                  <div>
                    <TruckInlineEdit
                      truckNumber={shipmentInfo.truckNumber}
                      shipmentId={shipmentId}
                      carrierId={carrierId}
                    />
                  </div>
                </div>
                {showInvalidP44TruckError && (
                  <InlineMessage
                    type={INLINE_MESSAGE_VARIANTS.ALERT}
                    message='Valid Project44 truck Number required for tracking with P44'
                  />
                )}
                {showInvalidKtTruckError && (
                  <InlineMessage
                    type={INLINE_MESSAGE_VARIANTS.ALERT}
                    message='Valid KeepTruckin truck Number required for tracking with KT'
                  />
                )}
              </div>
            </div>
          </>
        )}
        {coveredBid && (
          <div className='group1'>
            <p>Covered with</p>
            <CarrierPopover
              id={coveredBid.carrierApplicationId}
              legalName={coveredBid.carrierApplicationName}
              status={coveredBid.carrierApplication.stateName}
            />
          </div>
        )}
        {showReserveDetails && <SidebarReservedOfferDetails />}
        {!isArchived && !carrierId && <p>No Carrier Assigned</p>}
        <div className='group2'>
          <OpsInfo />
        </div>
      </div>
    </div>
  )
}
